import React from 'react';
import { Link } from 'react-router';
import { Points } from 'components/Points/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import UserHoverCard from 'components/UserHoverCard/UserHoverCard';
import { useHoverCard } from 'hooks/useHoverCard';
import './UserIdentityBlock.css';

export interface UserIdentityBlockProps {
  username: string;
  avatarUrl?: string | null;
  /** ISO country code for the flag badge; alpha-3 is normalized by AvatarWithFlag. */
  country?: string | null;
  /** Point balance. Omitted or null renders no balance row at all. */
  points?: number | null;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
  /** Avatar px; the CSS assumes 48 unless overridden. */
  size?: number;
  /**
   * Optional secondary line rendered in ONE fixed slot, directly under the
   * points row. This component owns placement only — the consumer owns the
   * child's styling. Not a general-purpose slot: do not use it to inject
   * additional structure, and do not add sibling slots.
   */
  children?: React.ReactNode;
  /** Extra class on the link wrapper (lets callers tweak spacing per context). */
  className?: string;
}

/**
 * The full-treatment user identity block: avatar (animated Elite border +
 * country flag) + username with Elite badge + point balance, all linking to the
 * profile and showing the UserHoverCard overlay on hover.
 *
 * Shared by the trade alerts views (by-card AlertCard and by-trader
 * AlertTradeGroup header) and the offer builder header, so the profile chrome
 * and its hover wiring live in exactly one place.
 *
 * The profile destination is deliberately NOT a prop: every consumer wants the
 * same one, and a parameterized destination invites deep links to surfaces that
 * cannot act on what they show.
 */
export const UserIdentityBlock: React.FC<UserIdentityBlockProps> = ({
  username,
  avatarUrl,
  country,
  points,
  isElite,
  showAnimatedBorder,
  borderColor1,
  borderColor2,
  size = 48,
  children,
  className,
}) => {
  // Hover overlay (trade metrics + badges) — same wiring as UsernameLink. Data is
  // fetched lazily by the hook against /users/:id/hover-card (no alerts-endpoint dep).
  const { triggerProps, cardProps, isVisible, position, data, isLoading } = useHoverCard(username);

  return (
    <>
      <Link
        className={`requester-profile ${className || ''}`}
        to={`/profile/u/${username}`}
        {...triggerProps}
      >
        <AvatarWithFlag
          avatarUrl={avatarUrl}
          username={username}
          size={size}
          countryCode={country}
          isElite={isElite}
          showAnimatedBorder={showAnimatedBorder}
          borderColor1={borderColor1}
          borderColor2={borderColor2}
        />
        <span className="requester-profile-info">
          <span className="requester-profile-name">
            {username}
            <EliteBadge isElite={!!isElite} />
          </span>
          {points != null && (
            <span className="requester-profile-points">
              <Points points={points} type="small" />
            </span>
          )}
          {children}
        </span>
      </Link>

      {isVisible && (
        <UserHoverCard
          data={data}
          isLoading={isLoading}
          position={position}
          cardProps={cardProps}
        />
      )}
    </>
  );
};

export default UserIdentityBlock;
